import { IconButton, Stack, Typography, Box, Table, TableContainer, TableBody, TableCell, TableHead, TableRow, Paper, Switch, FormControlLabel } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import React, { useEffect, useState } from "react";
import { SxProps } from "@mui/system";
import MetricsByMonthDialogue from "./MetricsByMonth/MetricsByMonth";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedMonthData, setSelectedMonthForecastContentSharedUid, setSelectedMetric, setSelectedMonth, setSelectedSite } from "../../../../../features/forecastersSlice";
import store from "../../../../../store";
import { Theme } from "@emotion/react";
import { setDisplayErrorSnackBar, setUserToken } from "../../../../../features/userSlice";
import { usePastForecast } from '../../../../../customHooks/usePastForecast';
import ForecastSkeleton from '../CreateNewForeCast/ForecastSkeleton';
import SimpleBackdrop from '../../../../BackDrop/BackDrop';

interface Props {
  selectedForecaster: any;
}

const GrowthInfo = ({ selectedForecaster }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const token = useSelector(setUserToken);
  const oauthTokenHeader = token?.payload?.user?.userToken;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedSiteFromRedux = useSelector((state: any) => state.forecasters?.selectedSite);
  const selectedSiteFromURL = searchParams.get("selectedSite");
  const selectedYear = searchParams.get("selectedYear") as any;
  const selectedSite = selectedSiteFromRedux || selectedSiteFromURL;
  const [openMetrics, setOpenMetrics] = useState(false);
  const [showOnlyForecasts, setShowOnlyForecasts] = useState(false);

  // Ensure 'page' param is '0' for this route
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/assets/overview/forecaster/forecasterInfo" &&
      newSearchParams.get("page") !== "0"
    ) {
      newSearchParams.set("page", "0");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate]);

  // Ensure selectedSite is set in Redux store when component mounts
  useEffect(() => {
    if (selectedSite && !selectedSiteFromRedux) {
      dispatch(setSelectedSite(selectedSite));
    }
  }, [selectedSite, selectedSiteFromRedux, dispatch]);

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth();
    const formattedMonth = `${selectedYear}-0${currentMonth + 1}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(
        setDisplayErrorSnackBar(
          "Please select a site.No selected site found in the URL"
        )
      );
    }
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  const tableheaders = [
    { id: 0, KEY: "month", label: "Month", metric: "month", sortable: true },
    { id: 1, KEY: "nrpu", label: "NRPU", metric: "nrpu", sortable: true },
    { id: 2, KEY: "ftd", label: "FTD", metric: "ftd", sortable: true },
    { id: 3, KEY: "avg_net_rev_per_active_user", label: "Avg Net Rev To Active Users", metric: "avg_net_rev_per_active_user", sortable: true },
    { id: 4, KEY: "cpa_pv", label: "CPA PV", metric: "cpa_pv", sortable: true },
    { id: 5, KEY: "cvr", label: "CVR", metric: "CVR", sortable: true },
    { id: 6, KEY: "profit", label: "Profit", metric: "profit", sortable: true },
    { id: 7, KEY: "active_users", label: "Active Users", metric: "active_users", sortable: true },
    { id: 8, KEY: "old_active_users", label: "Old Active Users", metric: "old", sortable: true },
    { id: 9, KEY: "acquired_active_users", label: "Acquired Users", metric: "aquired", sortable: true },
    { id: 10, KEY: "growth_target", label: "Growth Target", metric: "growth", sortable: true },
    { id: 11, KEY: "net_revenue", label: "Net Revenue", metric: "net_rev", sortable: true },
    { id: 12, KEY: "rs", label: "RS", metric: "rs_income_dollar", sortable: true },
    { id: 13, KEY: "rs_pv", label: "RS PV", metric: "rs_pv", sortable: true },
    { id: 14, KEY: "cpa", label: "CPA Income", metric: "cpa_income_dollar", sortable: true },
    { id: 15, KEY: "total_income", label: "Total Income", metric: "total_income", sortable: true },
    { id: 16, KEY: "clicks", label: "Clicks", metric: "clicks", sortable: true },
    { id: 17, KEY: "epc_cpa", label: "EPC CPA", metric: "EPC_CPA", sortable: true },
    { id: 18, KEY: "epc_rs", label: "EPC RS", metric: "EPC_RS", sortable: true },
    { id: 19, KEY: "epc", label: "EPC", metric: "EPC", sortable: true },
    { id: 20, KEY: "cpc", label: "CPC", metric: "CPC", sortable: true },
    { id: 21, KEY: "cac", label: "CAC", metric: "CAC", sortable: true },
    { id: 22, KEY: "spend", label: "Spend", metric: "spend", sortable: true },
    { id: 23, KEY: "avg_rs_deal_p", label: "Avg RS Percent", metric: "avg_rs_deal_p", sortable: true },
    { id: 24, KEY: "churn_rate", label: "Churn Rate", metric: "churn_rate", sortable: true },
    { id: 25, KEY: "br_to_ng_active_rs_ratio", label: "RS BR to BR Active Percent", metric: "br_to_ng_active_rs_ratio", sortable: true },
    { id: 26, KEY: "ratio_between_acquired_to_ftd", label: "Acquired to FTD", metric: "ratio_between_acquired_to_ftd", sortable: true },
  ];

  const sortedRows = selectedForecaster?.forecaster_content || [];

  const isMonthPassed = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // If viewing past year's forecast, all months are considered passed
    if (year < currentYear) {
      return true;
    }

    // If viewing future year's forecast, no months are considered passed
    if (year > currentYear) {
      return false;
    }

    // For current year, compare with current date
    const monthEndDate = new Date(year, month, 0);
    return currentDate > monthEndDate;
  };

  //(A) for months that have passed
  //(R) for the first month of the current quarter (if not passed)
  //(F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JS months are 0-based
    const currentDay = currentDate.getDate();

    // Case 1: If the month has already passed
    if (isMonthPassed(rowMonth)) {
      return "A";
    }

    // Case 2: If it's the current month
    if (year === currentYear && month === currentMonth) {
      return currentDay <= 15 ? "R" : "A"; // before or on 15th = R, after = A
    }

    // Case 3: Future months
    return "F";
  };

  const sortMonths = (rows: any[]) => {
    const monthOrder = {
      January: 1,
      February: 2,
      March: 3,
      April: 4,
      May: 5,
      June: 6,
      July: 7,
      August: 8,
      September: 9,
      October: 10,
      November: 11,
      December: 12,
    };

    return rows.sort((a, b) => {
      return (
        monthOrder[a.displayMonth as keyof typeof monthOrder] -
        monthOrder[b.displayMonth as keyof typeof monthOrder]
      );
    });
  };

  const modifiedRows = sortMonths(
    sortedRows.map((row: any) => {
      const [, month] = row.month.split("-").map(Number);
      const monthName = monthNames[month - 1];
      return { ...row, displayMonth: monthName };
    })
  );

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const formatNumber = (value: number | string) => {
    if (typeof value === "number") {
      return new Intl.NumberFormat().format(Math.round(value));
    }
    return value;
  };

  const formatPercentage = (value: any): string => {
    const num = Number(value);
    return isNaN(num) ? "0%" : `${Math.round(num * 100)}%`;
  };

  // when clicked display only the forecasted months (the months with 'F' indicator)
  const handleToggleDisplayForecasts = (event: any) => {
    setShowOnlyForecasts(event.target.checked);
  }

  const { forecastData, loading } = usePastForecast(modifiedRows, oauthTokenHeader, selectedSite);

  const CleanedHistoryData = forecastData.map((data: any) => {
    const cleanedItems = Object.fromEntries(Object.entries(data).filter(([key]) => !["input_params", "meta_data", "output_metadata"].includes(key)));
    return cleanedItems;
  });

  const getDeviation = (month: string, metricName: string) => {
    // 🧠 Check if the month has passed — only then include it
    if (!isMonthPassed(month)) return null;

    // ✅ Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.filter((item: any) => isMonthPassed(item.month)).find((item: any) => item.month === month);

    if (!monthData) return null;

    const metricItem = monthData.query_output.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase()
    );

    return metricItem?.Deviation ?? null;
  };

  return (
    <>
      {loading && forecastData.length === 0 ? (
        <ForecastSkeleton />
      ) : !loading && forecastData.length > 0 ? (
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px" }}>
          <Typography variant="h5" padding={"8px 16px 16px 16px"}>
            Metrics By Month
          </Typography>
          <FormControlLabel
            control={
              <Switch color="secondary" checked={showOnlyForecasts} onChange={handleToggleDisplayForecasts} />
            }
            label="Show only forecasts"
            labelPlacement="start"
          />
        </Box>
        <TableContainer className="FTDForecasterTable">
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{}}>
                {tableheaders.map((header) => {
                  // Decide which columns should be left-sticky
                  const isLeftSticky =
                    header.id === 0 ||
                    header.id === 1 ||
                    header.id === 2 ||
                    header.id === 3 ||
                    header.id === 4 ||
                    header.id === 5 ||
                    header.id === 6;
                  const leftOffsets: Record<number, number> = {
                    0: 0,
                    1: 114,
                    2: 201,
                    3: 277,
                    4: 508,
                    5: 606,
                    6: 683,
                  };

                  const cellSx: SxProps<Theme> = isLeftSticky
                    ? {
                      position: "sticky",
                      left: leftOffsets[header.id] ?? 0,
                      zIndex: 99,
                      backgroundColor: "#ffffff",
                      borderRight: header.id === 6 ? "1px solid #e0e0e0" : undefined,
                      color: header.id === 0 ? "#000000" : "#9C27B0",
                      padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                      whiteSpace: "nowrap",
                    } : {
                      color: "#000000",
                      padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                      whiteSpace: "nowrap",
                    };

                  return (
                    <TableCell key={header.id} align="left" sx={cellSx}>
                      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                        {header.label !== "Month" && header.label !== "More" ? (
                          <IconButton
                            color="secondary"
                            size="small"
                            data-tracker="131"
                            onClick={() => handleClickOpen(`${header.metric}`)}
                          >
                            <HistoryIcon
                              sx={{ fontSize: "1rem" }}
                              color="secondary"
                            />
                          </IconButton>
                        ) : null}
                        {
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "24px",
                              letterSpacing: "0.17px",
                            }}
                          >
                            {header.label}
                          </Typography>
                        }
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {modifiedRows?.filter((row) => !showOnlyForecasts || getMonthIndicator(row.month) === 'F')?.map((row: any, index: number) => (
                <React.Fragment key={index}>
                  <TableRow
                    sx={{
                      backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                      "& > *": {
                        borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
                      },
                      height: 35,
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: "0px 8px 0px 16px !important",
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {getMonthIndicator(row.month) === 'A' ? (
                          <RouterLink
                            to={`MonthlySummery?${(() => {
                              const params = new URLSearchParams(searchParams);
                              params.set("page", "0");
                              params.set("contentSharedUid", row?.content_shared_uid);
                              params.set("month", `${row?.displayMonth} ${selectedYear}`);
                              params.set("selectedMonth", row?.month);
                              return params.toString();
                            })()}`}
                            onClick={() => handleInfoIconClick(row)}
                            style={{ textDecoration: "none", color: "inherit", display: 'flex', gap: '4px' }}
                          >
                            <Typography variant="body2" color='secondary'>({getMonthIndicator(row.month)})</Typography>
                            {row?.displayMonth ? (
                              <Typography variant="body2" color='secondary' component="span" sx={{ textDecoration: 'underline' }}>{row.displayMonth}</Typography>
                            ) : ""}
                          </RouterLink>
                        ) : (
                          <>
                            <Typography variant="body2">({getMonthIndicator(row.month)})</Typography>
                            {row?.displayMonth ? (
                              <Typography variant="body2" component="span">{row.displayMonth}</Typography>
                            ) : ""}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "114px",
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.nrpu ? `${Math.round(parseFloat(row.nrpu))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "nrpu") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "nrpu") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "201px",
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.ftd ? formatNumber(parseFloat(row.ftd)) : ""}
                        </Typography>
                        {getDeviation(row.month, "ftd") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "ftd") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "277px",
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.avg_net_rev_per_active_user ? `${formatNumber(parseFloat(row.avg_net_rev_per_active_user))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "avg_net_rev_per_active_user") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "avg_net_rev_per_active_user") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "508px",
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.cpa_pv ? `${formatNumber(parseFloat(row.cpa_pv))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "cpa_pv") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "cpa_pv") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "606px",
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.cvr ? `${Math.round(parseFloat(row.cvr) * 100)}%` : ""}
                        </Typography>
                        {getDeviation(row.month, "CVR") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "CVR") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "0px 8px !important",
                        position: "sticky",
                        left: "683px",
                        zIndex: 2,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.profit ? `${formatNumber(parseFloat(row.profit))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "profit") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "profit") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.active_users ? formatNumber(parseFloat(row.active_users)) : ""}
                        </Typography>
                        {getDeviation(row.month, "active_users") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "active_users") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.old_active_users ? formatNumber(parseFloat(row.old_active_users)) : ""}
                        </Typography>
                        {getDeviation(row.month, "old_active_users") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "old_active_users") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.acquired_active_users ? formatNumber(parseFloat(row.acquired_active_users)) : ""}
                        </Typography>
                        {getDeviation(row.month, "acquired_active_users") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "acquired_active_users") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.growth_target ? `${Math.round(parseFloat(row.growth_target) * 100)}%` : ""}
                        </Typography>
                        {getDeviation(row.month, "growth") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "growth") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.net_revenue ? `${formatNumber(parseFloat(row.net_revenue))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "net_rev") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "net_rev") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.rs ? `${formatNumber(parseFloat(row?.rs))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "rs_income_dollar") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "rs_income_dollar") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.rs_pv ? `${formatNumber(parseFloat(row?.rs_pv))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "rs_pv") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "rs_pv") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.cpa ? `${formatNumber(parseFloat(row?.cpa))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "cpa_income_dollar") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "cpa_income_dollar") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.total_income ? `${formatNumber(parseFloat(row?.total_income))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "total_income") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "total_income") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.clicks ? `${Math.round(parseFloat(row.clicks))}` : ""}
                        </Typography>
                        {getDeviation(row.month, "clicks") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "clicks") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.epc_cpa ? `${formatNumber(parseFloat(row.epc_cpa))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "EPC_CPA") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "EPC_CPA") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.epc_rs ? `${formatNumber(parseFloat(row.epc_rs))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "EPC_RS") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "EPC_RS") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.epc ? `${formatNumber(parseFloat(row.epc))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "EPC") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "EPC") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.cpc ? `${formatNumber(parseFloat(row.cpc))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "CPC") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "CPC") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.cac ? `${formatNumber(parseFloat(row.cac))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "CAC") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "CAC") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: "#F5F5F5",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {row?.spend ? `${formatNumber(parseFloat(row.spend))}$` : ""}
                        </Typography>
                        {getDeviation(row.month, "spend") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "spend") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor:isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {formatPercentage(row?.avg_rs_deal_p)}
                        </Typography>
                        {getDeviation(row.month, "avg_rs_p") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "avg_rs_p") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {Math.round(Number(row?.churn_rate) * 100)}%
                        </Typography>
                        {getDeviation(row.month, "churn_rate") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "churn_rate") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {Math.round(Number(row?.br_to_ng_active_rs_ratio) * 100)}%
                        </Typography>
                        {getDeviation(row.month, "rs_br_ng") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "rs_br_ng") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2">
                          {Math.round(Number(row?.ratio_between_acquired_to_ftd) * 100)}%
                        </Typography>
                        {getDeviation(row.month, "Ratio_between_acquired_to_ftd") !== null && (
                          <Typography variant="body2" color='#00000099'>
                            ({(getDeviation(row.month, "Ratio_between_acquired_to_ftd") * 100).toFixed(1)}%)
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center"></Stack>
        </TableContainer>
        <MetricsByMonthDialogue open={openMetrics} handleClose={handleClose} selectedSiteFromParent={selectedSite} />
      </Paper>
      ) : (
        <SimpleBackdrop />
      )}
    </>
  )
}

export default GrowthInfo
